import { inject, Injectable } from "@angular/core";
import { STORE_NAME_METADATA, STORE_NAME_PROJECTS } from "@app/indexed-db-config";
import { CloudProjectMetadata } from "@domain/project/cloud-project-metadata";
import {
  getCloudId,
  getProjectId,
  isValidMetadataScope,
  ProjectAggregate,
  ProjectAggregateIssue,
  ProjectAggregateIssueKind,
  ProjectAggregateLoadResult,
  ProjectAggregateRepairResult,
} from "@domain/project/persistence/project-aggregate";
import { deserializeProject, inferProjectScopeFromPayload } from "@domain/project/project-deserializer";
import type { ScopedProject } from "@domain/project/project-guards";
import { BusinessScope } from "@lib/constants/business-scope";
import { instanceToPlain, plainToInstance } from "class-transformer";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { catchError, forkJoin, map, Observable, of, switchMap, throwError } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ProjectAggregateRepository {
  private dbService = inject(NgxIndexedDBService);
  private deserializeProjectEntry(plain: Record<string, unknown>, scope: BusinessScope) {
    return deserializeProject(plain, scope);
  }

  private inferProjectScopeFromPayloadEntry(plain: Record<string, unknown>): BusinessScope | undefined {
    return inferProjectScopeFromPayload(plain);
  }

  loadAggregates(userScope?: BusinessScope): Observable<ProjectAggregateLoadResult> {
    return forkJoin([
      this.dbService.getAll<Record<string, unknown>>(STORE_NAME_PROJECTS),
      this.dbService.getAll<CloudProjectMetadata>(STORE_NAME_METADATA),
    ]).pipe(map(([rawProjects, metadata]) => this.mapRawAggregates(rawProjects, metadata, userScope)));
  }

  saveAggregate(project: ScopedProject, metadata?: CloudProjectMetadata): Observable<void> {
    return this.validateSaveInvariant(project, metadata).pipe(
      switchMap((effectiveMetadata) =>
        forkJoin({
          previousProject: this.dbService.getByID(STORE_NAME_PROJECTS, project.id),
          previousMetadata: effectiveMetadata
            ? this.dbService.getByKey(STORE_NAME_METADATA, effectiveMetadata.id)
            : of(undefined),
        }).pipe(
          switchMap(({ previousProject, previousMetadata }) =>
            this.upsertProject(project).pipe(
              switchMap(() => (effectiveMetadata ? this.upsertMetadata(effectiveMetadata) : of(void 0))),
              map(() => void 0),
              catchError((error) =>
                this.rollbackAggregateWrite(project, effectiveMetadata, previousProject, previousMetadata).pipe(
                  switchMap(() => throwError(() => error)),
                ),
              ),
            ),
          ),
        ),
      ),
    );
  }

  deleteAggregate(projectId: string, cloudId?: string): Observable<void> {
    const deleteProject$ = this.dbService.deleteByKey(STORE_NAME_PROJECTS, projectId);
    const deleteMetadata$ = cloudId ? this.dbService.deleteByKey(STORE_NAME_METADATA, cloudId) : of(undefined);

    return forkJoin([deleteProject$, deleteMetadata$]).pipe(map(() => void 0));
  }

  clearProjectAndMetadataStores(): Observable<void> {
    return forkJoin([this.dbService.clear(STORE_NAME_PROJECTS), this.dbService.clear(STORE_NAME_METADATA)]).pipe(
      map(() => void 0),
    );
  }

  cleanupMetadata(metadataIds: string[]): Observable<void> {
    if (metadataIds.length === 0) {
      return of(void 0);
    }

    return forkJoin(metadataIds.map((metadataId) => this.dbService.deleteByKey(STORE_NAME_METADATA, metadataId))).pipe(
      map(() => void 0),
    );
  }

  repairMissingMetadata(
    cloudIds: string[],
    resolveMetadata: (cloudId: string) => Observable<CloudProjectMetadata | undefined>,
  ): Observable<ProjectAggregateRepairResult> {
    if (cloudIds.length === 0) {
      return of({ attemptedCloudIds: [], repairedCloudIds: [] });
    }

    return forkJoin(
      cloudIds.map((cloudId) =>
        resolveMetadata(cloudId).pipe(
          catchError(() => of(undefined)),
          map((metadata) => ({ cloudId, metadata })),
        ),
      ),
    ).pipe(
      switchMap((fetched) => {
        const validMetadata = fetched
          .filter((entry) => !!entry.metadata && isValidMetadataScope(entry.metadata.businessScope))
          .map((entry) => entry.metadata!);

        if (validMetadata.length === 0) {
          return of({
            attemptedCloudIds: cloudIds,
            repairedCloudIds: [] as string[],
          });
        }

        return forkJoin(validMetadata.map((metadata) => this.upsertMetadata(metadata))).pipe(
          map(() => ({
            attemptedCloudIds: cloudIds,
            repairedCloudIds: validMetadata.map((metadata) => metadata.id),
          })),
        );
      }),
    );
  }

  private mapRawAggregates(
    rawProjects: Record<string, unknown>[],
    metadata: CloudProjectMetadata[],
    userScope?: BusinessScope,
  ): ProjectAggregateLoadResult {
    const issues: ProjectAggregateIssue[] = [];
    const aggregates: ProjectAggregate[] = [];
    const metadataInstances = metadata.map((entry) => plainToInstance(CloudProjectMetadata, entry));
    const metadataById = new Map(metadataInstances.map((entry) => [entry.id, entry]));
    const projectCloudIds = new Set<string>();
    const repairableCloudIds: string[] = [];

    rawProjects.forEach((rawProject) => {
      const projectId = getProjectId(rawProject);
      const cloudId = getCloudId(rawProject);

      if (cloudId) {
        projectCloudIds.add(cloudId);
        const matchingMetadata = metadataById.get(cloudId);

        if (!matchingMetadata) {
          repairableCloudIds.push(cloudId);
          issues.push({
            kind: ProjectAggregateIssueKind.CLOUD_PROJECT_MISSING_METADATA,
            projectId,
            cloudId,
            message: `Cloud-linked project ${projectId ?? "<unknown>"} is missing metadata for cloudId ${cloudId}`,
          });
          return;
        }

        if (matchingMetadata.id !== cloudId) {
          issues.push({
            kind: ProjectAggregateIssueKind.METADATA_ID_MISMATCH,
            projectId,
            cloudId,
            metadataId: matchingMetadata.id,
            message: `Cloud-linked project ${projectId ?? "<unknown>"} has metadata mismatch (${matchingMetadata.id} != ${cloudId})`,
          });
          return;
        }

        if (!isValidMetadataScope(matchingMetadata.businessScope)) {
          repairableCloudIds.push(cloudId);
          issues.push({
            kind: ProjectAggregateIssueKind.INVALID_METADATA_SCOPE,
            projectId,
            cloudId,
            metadataId: matchingMetadata.id,
            message: `Cloud-linked project ${projectId ?? "<unknown>"} has invalid metadata scope`,
          });
          return;
        }

        try {
          const project = this.deserializeProjectEntry(rawProject, matchingMetadata.businessScope);
          aggregates.push({ project, metadata: matchingMetadata });
        } catch (error) {
          issues.push({
            kind: ProjectAggregateIssueKind.DESERIALIZATION_FAILED,
            projectId,
            cloudId,
            metadataId: matchingMetadata.id,
            message: `Cloud-linked project ${projectId ?? "<unknown>"} failed deserialization: ${String(error)}`,
          });
        }
        return;
      }

      const resolvedLocalScope = isValidMetadataScope(userScope) ? userScope : this.inferProjectScopeFromPayloadEntry(rawProject);
      if (!isValidMetadataScope(resolvedLocalScope)) {
        issues.push({
          kind: ProjectAggregateIssueKind.LOCAL_PROJECT_MISSING_USER_SCOPE,
          projectId,
          message: `Unsynced local project ${projectId ?? "<unknown>"} cannot be deserialized without user scope or inferrable payload scope`,
        });
        return;
      }

      try {
        const project = this.deserializeProjectEntry(rawProject, resolvedLocalScope);
        aggregates.push({ project });
      } catch (error) {
        issues.push({
          kind: ProjectAggregateIssueKind.DESERIALIZATION_FAILED,
          projectId,
          message: `Unsynced local project ${projectId ?? "<unknown>"} failed deserialization: ${String(error)}`,
        });
      }
    });

    const reverseOrphanMetadataIds = metadataInstances.filter((entry) => !projectCloudIds.has(entry.id)).map((entry) => entry.id);

    reverseOrphanMetadataIds.forEach((metadataId) => {
      issues.push({
        kind: ProjectAggregateIssueKind.METADATA_WITHOUT_PROJECT,
        metadataId,
        cloudId: metadataId,
        message: `Metadata ${metadataId} does not have a matching local project`,
      });
    });

    return {
      aggregates,
      issues,
      repairableCloudIds,
      reverseOrphanMetadataIds,
    };
  }

  private validateSaveInvariant(
    project: ScopedProject,
    metadata?: CloudProjectMetadata,
  ): Observable<CloudProjectMetadata | undefined> {
    if (!project.cloudId) {
      if (metadata) {
        return throwError(() => new Error("Unsynced local project must not persist cloud metadata"));
      }
      return of(undefined);
    }

    if (metadata) {
      if (metadata.id !== project.cloudId) {
        return throwError(
          () =>
            new Error(
              `Cloud-linked project ${project.id} has mismatching metadata id (${metadata.id}) and cloud id (${project.cloudId})`,
            ),
        );
      }
      if (!isValidMetadataScope(metadata.businessScope)) {
        return throwError(() => new Error(`Cloud-linked project ${project.id} has invalid metadata scope`));
      }
      return of(metadata);
    }

    return this.dbService.getByKey<CloudProjectMetadata>(STORE_NAME_METADATA, project.cloudId).pipe(
      map((rawMetadata) => (rawMetadata ? plainToInstance(CloudProjectMetadata, rawMetadata) : undefined)),
      switchMap((storedMetadata) => {
        if (!storedMetadata) {
          return throwError(
            () =>
              new Error(
                `Cloud-linked project ${project.id} cannot be saved because metadata ${project.cloudId} is missing in local storage`,
              ),
          );
        }
        if (!isValidMetadataScope(storedMetadata.businessScope)) {
          return throwError(
            () =>
              new Error(
                `Cloud-linked project ${project.id} cannot be saved because metadata ${project.cloudId} has invalid scope`,
              ),
          );
        }
        return of(undefined);
      }),
    );
  }

  private upsertProject(project: ScopedProject): Observable<void> {
    return this.dbService.getByID(STORE_NAME_PROJECTS, project.id).pipe(
      switchMap((existingProject) => {
        const plainProject = instanceToPlain(project);
        return existingProject
          ? this.dbService.update(STORE_NAME_PROJECTS, plainProject)
          : this.dbService.add(STORE_NAME_PROJECTS, plainProject);
      }),
      map(() => void 0),
    );
  }

  private upsertMetadata(metadata: CloudProjectMetadata): Observable<void> {
    return this.dbService.getByKey(STORE_NAME_METADATA, metadata.id).pipe(
      switchMap((existingMetadata) => {
        if (existingMetadata) {
          return this.dbService.update(STORE_NAME_METADATA, instanceToPlain(metadata));
        }
        return this.dbService.add(STORE_NAME_METADATA, instanceToPlain(metadata));
      }),
      map(() => void 0),
    );
  }

  private rollbackAggregateWrite(
    project: ScopedProject,
    metadata: CloudProjectMetadata | undefined,
    previousProject: unknown,
    previousMetadata: unknown,
  ): Observable<void> {
    const rollbackProject$ = previousProject
      ? this.dbService.update(STORE_NAME_PROJECTS, previousProject as Record<string, unknown>)
      : this.dbService.deleteByKey(STORE_NAME_PROJECTS, project.id);
    const rollbackMetadata$ = metadata
      ? previousMetadata
        ? this.dbService.update(STORE_NAME_METADATA, previousMetadata as Record<string, unknown>)
        : this.dbService.deleteByKey(STORE_NAME_METADATA, metadata.id)
      : of(undefined);

    return forkJoin([rollbackProject$, rollbackMetadata$]).pipe(
      map(() => void 0),
      catchError((rollbackError) => {
        console.error("Failed to rollback aggregate write", rollbackError);
        return of(void 0);
      }),
    );
  }
}
